/**
 * Conciliación Bancaria - Finance 360
 * Bank reconciliation with AI matching
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Upload,
  RefreshCw,
  CheckCircle2,
  Clock,
  XCircle,
  Sparkles,
  Link2,
  Trash2,
  Plus,
  FileText,
  AlertTriangle,
  ChevronDown,
  Search
} from 'lucide-react';
import { contabilidadApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import LoadingSpinner, { LoadingPage } from '../../components/LoadingSpinner';
import ConfirmModal from '../../components/ConfirmModal';

export default function ConciliacionBancaria() {
  const { id } = useParams();
  const { currentEmisor } = useAuthStore();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [cuenta, setCuenta] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [sugerencias, setSugerencias] = useState([]);
  const [importing, setImporting] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Filters
  const [filtroEstado, setFiltroEstado] = useState('pendiente');

  // Modal states
  const [showAsientoModal, setShowAsientoModal] = useState(false);
  const [selectedMovimiento, setSelectedMovimiento] = useState(null);
  const [cuentasContables, setCuentasContables] = useState([]);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning' });

  useEffect(() => {
    if (currentEmisor && id) {
      loadData();
    }
  }, [currentEmisor, id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cuentaRes, resumenRes, movRes, sugerenciasRes, cuentasRes] = await Promise.all([
        contabilidadApi.getBanco(id),
        contabilidadApi.getBancoResumen(id),
        contabilidadApi.getBancoMovimientos(id, { estado: filtroEstado }),
        contabilidadApi.getBancoSugerencias(id),
        contabilidadApi.getCuentas({ flat: true, soloMovimiento: true })
      ]);

      setCuenta(cuentaRes.data.data);
      setResumen(resumenRes.data.data);
      setMovimientos(movRes.data.data);
      setSugerencias(sugerenciasRes.data.data || []);
      setCuentasContables(cuentasRes.data.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('archivo', file);

    setImporting(true);
    try {
      const response = await contabilidadApi.importarExtracto(id, formData);
      toast.success(`Importados: ${response.data.data.importados} movimientos`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al importar');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleConciliar = async (movimientoId, asientoId) => {
    try {
      await contabilidadApi.conciliarMovimiento(movimientoId, { asientoId });
      toast.success('Movimiento conciliado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al conciliar');
    }
  };

  const handleDescartar = (movimientoId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Descartar Movimiento',
      message: '¿Descartar este movimiento? No se incluirá en la conciliación.',
      variant: 'warning',
      onConfirm: async () => {
        try {
          await contabilidadApi.descartarMovimiento(movimientoId);
          toast.success('Movimiento descartado');
          loadData();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Error al descartar');
        }
      }
    });
  };

  const handleAutoConciliar = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Auto-Conciliación',
      message: '¿Conciliar automáticamente los movimientos con alta confianza (>90%)?',
      variant: 'info',
      onConfirm: async () => {
        setProcessing(true);
        try {
          const response = await contabilidadApi.autoConciliar(id);
          toast.success(`Conciliados automáticamente: ${response.data.data.conciliados} movimientos`);
          loadData();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Error en auto-conciliación');
        } finally {
          setProcessing(false);
        }
      }
    });
  };

  const handleCrearAsiento = async (cuentaContrapartidaId) => {
    if (!selectedMovimiento) return;

    try {
      await contabilidadApi.crearAsientoDesdeMovimiento(selectedMovimiento.id, {
        cuentaContrapartidaId
      });
      toast.success('Asiento creado y movimiento conciliado');
      setShowAsientoModal(false);
      setSelectedMovimiento(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al crear asiento');
    }
  };

  const formatCurrency = (amount, moneda = 'CRC') => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: moneda,
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (!cuenta) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-red-300 mx-auto mb-3" />
        <p className="text-gray-500">Cuenta no encontrada</p>
      </div>
    );
  }

  const highConfidenceSugerencias = sugerencias.filter(s => s.confianza >= 90);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/contabilidad/bancos"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Conciliación Bancaria</h1>
            <p className="text-gray-500">{cuenta.nombre} - {cuenta.banco}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.ofx"
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {importing ? <LoadingSpinner size="sm" /> : <Upload className="h-4 w-4" />}
            Importar Extracto
          </button>

          {highConfidenceSugerencias.length > 0 && (
            <button
              onClick={handleAutoConciliar}
              disabled={processing}
              className="flex items-center gap-2 px-4 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 disabled:opacity-50"
            >
              {processing ? <LoadingSpinner size="sm" /> : <Sparkles className="h-4 w-4" />}
              Auto-conciliar ({highConfidenceSugerencias.length})
            </button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500">Saldo Libros</p>
          <p className="text-xl font-bold text-gray-900">
            {formatCurrency(resumen?.saldoLibros, cuenta.moneda)}
          </p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500">Saldo Banco</p>
          <p className="text-xl font-bold text-gray-900">
            {formatCurrency(resumen?.saldoBanco, cuenta.moneda)}
          </p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500">Diferencia</p>
          <p className={`text-xl font-bold ${
            Math.abs(resumen?.diferencia || 0) < 1 ? 'text-green-600' : 'text-red-600'
          }`}>
            {formatCurrency(resumen?.diferencia, cuenta.moneda)}
          </p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <p className="text-xs text-gray-500">Pendientes</p>
          <p className="text-xl font-bold text-yellow-600">
            {resumen?.movimientos?.pendientes || 0}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 bg-white p-4 rounded-xl border">
        <select
          value={filtroEstado}
          onChange={(e) => { setFiltroEstado(e.target.value); }}
          className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="pendiente">Pendientes</option>
          <option value="conciliado">Conciliados</option>
          <option value="descartado">Descartados</option>
          <option value="">Todos</option>
        </select>

        <button
          onClick={loadData}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          <RefreshCw className="h-5 w-5" />
        </button>
      </div>

      {/* Movements Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        {movimientos.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-medium text-gray-900">No hay movimientos</h3>
            <p className="text-sm text-gray-500 mt-1">
              Importe un extracto bancario para comenzar
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Fecha
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Descripción
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Monto
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  Estado
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  Sugerencia AI
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {movimientos.map((mov) => {
                const sugerencia = sugerencias.find(s => s.movimientoId === mov.id);

                return (
                  <tr key={mov.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(mov.fecha).toLocaleDateString('es-CR')}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900 line-clamp-1">{mov.descripcion}</p>
                      {mov.referencia && (
                        <p className="text-xs text-gray-400">Ref: {mov.referencia}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-medium ${
                        mov.tipo === 'credito' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {mov.tipo === 'credito' ? '+' : '-'}
                        {formatCurrency(mov.monto, cuenta.moneda)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                        mov.estado === 'conciliado' ? 'bg-green-100 text-green-700' :
                        mov.estado === 'descartado' ? 'bg-gray-100 text-gray-600' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {mov.estado === 'conciliado' && <CheckCircle2 className="h-3 w-3" />}
                        {mov.estado === 'pendiente' && <Clock className="h-3 w-3" />}
                        {mov.estado === 'descartado' && <XCircle className="h-3 w-3" />}
                        {mov.estado}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {sugerencia && mov.estado === 'pendiente' && (
                        <div className="flex items-center justify-center gap-2">
                          <Sparkles className="h-4 w-4 text-purple-500" />
                          <span className={`text-xs font-medium ${
                            sugerencia.confianza >= 90 ? 'text-green-600' :
                            sugerencia.confianza >= 70 ? 'text-yellow-600' : 'text-gray-500'
                          }`}>
                            {sugerencia.confianza}%
                          </span>
                          <button
                            onClick={() => handleConciliar(mov.id, sugerencia.asientoId)}
                            className="text-xs text-blue-600 hover:text-blue-700"
                          >
                            Aplicar
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {mov.estado === 'pendiente' && (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setSelectedMovimiento(mov);
                              setShowAsientoModal(true);
                            }}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="Crear asiento"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDescartar(mov.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Descartar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      {mov.estado === 'conciliado' && mov.asientoId && (
                        <Link
                          to={`/contabilidad/asientos/${mov.asientoId}`}
                          className="text-xs text-blue-600 hover:text-blue-700"
                        >
                          Ver asiento
                        </Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Asiento Modal */}
      {showAsientoModal && selectedMovimiento && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Crear Asiento desde Movimiento</h2>
              <p className="text-sm text-gray-500 mt-1">{selectedMovimiento.descripcion}</p>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Monto</span>
                  <span className={`font-bold ${
                    selectedMovimiento.tipo === 'credito' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {selectedMovimiento.tipo === 'credito' ? '+' : '-'}
                    {formatCurrency(selectedMovimiento.monto, cuenta.moneda)}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cuenta de Contrapartida
                </label>
                <select
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) {
                      handleCrearAsiento(parseInt(e.target.value));
                    }
                  }}
                >
                  <option value="">Seleccione una cuenta...</option>
                  {cuentasContables.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.codigo} - {c.nombre}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Se creará un asiento con débito/crédito a la cuenta del banco y contrapartida a esta cuenta
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t">
              <button
                onClick={() => {
                  setShowAsientoModal(false);
                  setSelectedMovimiento(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText="Confirmar"
      />
    </div>
  );
}
